<template>
	<div class="webhooks-list">
		<AppList :items="webhooksItems">
			<template #new-webhook-slot="{webhookItem}">
				<div class="row mb-8 mt-4 row-explain">
					<p>{{ getString('webhook_dev_explain') }}<a class="ml-6" href="https://qromo.stoplight.io/docs/qromo-webhooks/v7brryyjbfvgf-qromo-webhook" target="_blank">{{ getString('documentation') }}</a></p>
				</div>
				<AppList :items="newWebhookItems" >
					<template #edit-events-slot="{item}">
						<div class="edit-events-container">
							<p>{{ item.title }}</p>
							<div class="actions">
								<p class="counter">{{ getString('selected_types', [newWebhookSelectedEventTypes.length]) }}</p>
								<AppButton :text="getString('edit')" outline @click="editWebhookEvents" small />
							</div>
						</div>
					</template>
				</AppList>
			</template>
			<template #webhook-slot="{item}">
				<div class="row webhook-container">
					<p>{{ item.title }}</p>
					<div class="webhook-data-edit">
						<div class="webhook-data">
							<div class="row space-between webhook-data-headline">
								<div class="webhook-data-type">
									<p class="webhook-data-url">{{ item.url }}</p>
									<p class="webhook-data-auth">{{ item.auth }}</p>
								</div>
								<AppButtonIcon icon-name="icon-edit-white" prevent-change @click-icon="startEditingWebhook(item)" />
							</div>
							<div class="row events-scroller" v-if="item.types.length > 0">
								<div class="event" v-for="type in item.types" :key="type">
									{{getString(type)}}
								</div>
							</div>
						</div>
						<AppButton :text="getString('log')" outline @click="showWebhookActive(item)" />
					</div>
				</div>
			</template>
		</AppList>
	</div>

	<AppBottomSheet :active="showLog !== null" @toggling-popup="handleBottomSheetToggle" :force-width="bottomSheetWidth">
		<WebhookLog v-if="showLog !== null" :webhook="showLog" @fetch-error="errorFetchingLog"/>
	</AppBottomSheet>
	<AppBottomSheet :active="editingWebhook" @toggling-popup="toggleWebhookTypesBottomSheet" :force-width="bottomSheetWidth">
		<ModalHeader :title="getString('webhook_type')" :button-text="getString('confirm')" @click-button="updateWebhookEvents" />
		<AppList :items="webhookTypesList" />
	</AppBottomSheet>

	<AppPopup ref="delete-webhook-popup"
		:title="getString('confirm_delete_webhook_title')"
		:body-text="getString('confirm_delete_webhook_body')"
		:buttons="deletePopupButtons"
	/>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import {useUserStore} from "@/console-new/stores";
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import {useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import AppButton from "@/shared/components/AppButton.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import WebhookLog from "@/console-new/components/config/settings/general/WebhookLog.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppPopup from "@/shared/components/AppPopup.vue";

export default {
	setup() {
		let userStore = useUserStore();
		let languageStore = useLanguageStore();
		let notificationStore = useNotificationStore();
		let svgStore = useSvgStore();
		return {
			userStore,
			notificationStore,
			getIcon: svgStore.get,
			getString: languageStore.getString,
		}
	},
	mixins: [WindowWidth],
	name: "WebhooksList",
	components: {ModalHeader, AppButton, AppChevronIcon, AppList, AppBottomSheet, WebhookLog, AppButtonIcon, AppPopup},
	data() {
		return {
			newWebhookActive: false,
			loadingWebhooks: false,
			showLog: null,
			newWebhookName: null,
			newWebhookUrl: null,
			editingWebhook: false,
			editingWebhookId: null,
			webhookTypesMap: [
				{value: "orders.new"},
				{value: "orders.update"},
				{value: "reservations.new"},
				{value: "reservations.update"}
			],
			newWebhookSelectedEventTypes: [],
			editingWebhookSelectedEventTypes: [],
			loadingDeleteWebhook: false,
			eventTypeBottomSheetTriggeredFromAdd: false,
		}
	},
	computed: {
		webhooksItems() {
			let items = [];
			if(this.userStore.currBusiness && this.userStore.currBusiness.webhooks) {
				for(let i = 0; i < this.userStore.currBusiness.webhooks.length; i++) {
					let webhook = this.userStore.currBusiness.webhooks[i];
					items.push({
						title: webhook.name,
						types: this.userStore.currBusiness.webhook_types[webhook.business_webhook_id] ?? [],
						customSlot: "webhook-slot",
						url: webhook.url,
						auth: webhook.auth,
						type: this.webhookTypesMap.find(type => type.value === webhook.type)?.label || null,
						webhook_id: webhook.business_webhook_id,
						loading: false,
					});
				}
			}
			items.push({
				title: this.getString('new_webhook'),
				chevronItem: true,
				dropdownSlot: "new-webhook-slot",
				active: this.newWebhookActive,
				switch: false,
				callback: () => {
					this.newWebhookActive = !this.newWebhookActive;
				}
			})
			return items;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		newWebhookItems() {
			return [
				{
					title: this.getString('webhook_name'),
					textInput: true,
					placeholder: this.getString('webhook_name'),
					value: this.newWebhookName,
					callbackText: v => this.newWebhookName = v
				},
				{
					title: this.getString('webhook_url'),
					textInput: true,
					placeholder: this.getString('webhook_url_placeholder'),
					value: this.newWebhookUrl,
					callbackText: v => this.newWebhookUrl = v
				},
				{
					title: this.getString('webhook_type'),
					customSlot: 'edit-events-slot',
				},
				{
					title: '',
					buttonTitle: this.getString('add'),
					callbackButton: this.addNewWebhook,
					buttonLoading: this.loadingWebhooks
				},
			];
		},
		webhookTypesList() {
			const targetEventTypes = this.editingWebhookId !== null ? this.editingWebhookSelectedEventTypes : this.newWebhookSelectedEventTypes;

			let list = this.webhookTypesMap.map(type => {
				return {
					title: this.getString(type.value),
					switch: true,
					active: targetEventTypes.includes(type.value),
					callback: active => {
						if (this.editingWebhookId !== null) {
							if (active) {
								this.editingWebhookSelectedEventTypes.push(type.value);
							} else {
								this.editingWebhookSelectedEventTypes = this.editingWebhookSelectedEventTypes.filter(item => item !== type.value);
							}
						} else {
							if (active) {
								this.newWebhookSelectedEventTypes.push(type.value);
							} else {
								this.newWebhookSelectedEventTypes = this.newWebhookSelectedEventTypes.filter(item => item !== type.value);
							}
						}
					}
				};
			});

			if (!this.eventTypeBottomSheetTriggeredFromAdd) {
				list.push({
					title: this.getString("webhook_delete_title"),
					buttonTitle: this.getString("delete"),
					buttonLoading: this.loadingDeleteWebhook,
					callbackButton: () => {
						this.$refs['delete-webhook-popup'].toggleStatus();
					}
				})
			}

			return list;
		},
		deletePopupButtons() {
			return [
				{text: this.getString('cancel'), negative: true},
				{text: this.getString('delete'), loading: this.loadingDeleteWebhook, callback: this.confirmDeleteWebhook}
			];
		}
	},
	methods: {
		addNewWebhook() {
			if(!this.$refs) {
				return;
			}

			if(this.userStore?.currBusiness?.webhooks?.length && this.userStore?.currSubscription && ['pro', 'allinone'].includes(this.userStore?.currSubscription)) {
				this.notificationStore.showNotification('webhook-cant');
				return;
			} else if(!this.userStore?.currSubscription || ['base', 'basic'].includes(this.userStore?.currSubscription)) {
				this.notificationStore.showNotification('webhook-zero');
				return;
			}

			let name = this.newWebhookName;
			let url = this.newWebhookUrl;
			let types = this.newWebhookSelectedEventTypes;

			if(!name || name.length < 3) {
				this.notificationStore.showNotification("invalid-name");
				return;
			}
			else if (types.length === 0) {
				this.notificationStore.showNotification('webhook-type-zero')
				return;
			}

			if(name && url && types) {
				try {
					let testUrl = new URL(url);
					const ipv4Regex = /^(?:(?:(?:[a-zA-z\-]+)\:\/{1,3})?(?:[a-zA-Z0-9])(?:[a-zA-Z0-9\-\.]){1,61}(?:\.[a-zA-Z]{2,})+|\[(?:(?:(?:[a-fA-F0-9]){1,4})(?::(?:[a-fA-F0-9]){1,4}){7}|::1|::)\]|(?:(?:[0-9]{1,3})(?:\.[0-9]{1,3}){3}))(?:\:[0-9]{1,5})?$/
					if(testUrl.protocol === "https:" && ipv4Regex.test(testUrl.hostname) && (!testUrl.port || testUrl.port === "443")) {
						this.loadingWebhooks = true;
						this.userStore.addNewWebhook(name, url, types, (r, details) => {
							if(r && details) {
								let newWebhookId = Number(details.webhook_id);
								let auth = details.auth;
								this.userStore.refreshBusiness(this.userStore.currBusinessId, () => {
									this.loadingWebhooks = false;
									this.newWebhookActive = false;

									let newWebhook = this.userStore.currBusiness.webhooks.find(w => w.business_webhook_id === newWebhookId);
									if(newWebhook) {
										newWebhook.auth = auth;
									}
								})
							} else {
								this.loadingWebhooks = false;
								if(details && details === "webhook_limit") {
									this.notificationStore.showNotification("webhook-limit");
								} else if(details && details === "webhook_exists") {
									this.notificationStore.showNotification("webhook-exists");
								} else if(details && details === "webhook_cant") {
									this.notificationStore.showNotification('webhook-cant');
								} else if(details && details === "webhook_zero") {
									this.notificationStore.showNotification('webhook-zero');
								} else {
									this.notificationStore.showNotification("generic-fail");
								}
							}

							this.newWebhookName = null;
							this.newWebhookUrl = null;
							this.newWebhookSelectedEventTypes = [];
						});
					} else {
						this.notificationStore.showNotification("invalid-url");
					}
				} catch (_) {
					this.notificationStore.showNotification("invalid-url");
				}
			} else {
				this.notificationStore.showNotification("invalid-url");
			}
		},
		handleBottomSheetToggle(state) {
			if(!state) {
				this.showLog = null;
			}
		},
		showWebhookActive(webhook) {
			this.showLog = webhook;
		},
		errorFetchingLog() {
			this.notificationStore.showNotification("generic-fail");
			this.handleBottomSheetToggle(false)
		},
		toggleWebhookTypesBottomSheet(type) {
			if(!type) {
				this.editingWebhook = false;
				this.editingWebhookId = null;
				// timeout set to prevent UI glitch when closing after opening it bottomsheet in the add flow
				setTimeout(() => this.eventTypeBottomSheetTriggeredFromAdd = false, 100);
			}
		},
		confirmDeleteWebhook() {
			this.loadingDeleteWebhook = true;
			this.userStore.deleteWebhook(this.editingWebhookId, (r) => {
				this.$refs['delete-webhook-popup'].toggleStatus();
				this.loadingDeleteWebhook = false;
				if (r) {
					this.notificationStore.showNotification("webhook-deleted");
					this.toggleWebhookTypesBottomSheet(false);
					this.userStore.refreshBusiness(this.userStore.currBusinessId);
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		},
		editWebhookEvents() {
			this.eventTypeBottomSheetTriggeredFromAdd = true;
			this.editingWebhook = true;
		},
		startEditingWebhook(webhook) {
			this.editingWebhookId = webhook.webhook_id;
			this.editingWebhookSelectedEventTypes = [...(this.userStore.currBusiness?.webhook_types[webhook.webhook_id] ?? [])];
			this.editingWebhook = true;
		},
		updateWebhookEvents() {
			if (this.editingWebhookId) {
				this.userStore.updateWebhookTypes(this.editingWebhookId, this.editingWebhookSelectedEventTypes, (r) => {
					if (r) {
						this.userStore.refreshBusiness(this.userStore.currBusinessId);
						this.toggleWebhookTypesBottomSheet(false);
					} else {
						this.notificationStore.showNotification("generic-fail");
					}
				});
			}
			else {
				this.editingWebhook = false;
			}
		}
	}
}
</script>

<style scoped lang="scss">

.webhooks-list {

	:deep(.dropdown-content) {
		background-color: var(--color-bg-primary-2);
		box-shadow: 0 2px 3px rgba(0, 0, 0, 0.19);
		padding: 0 10px;
	}

	:deep(.list-item) {
		padding: 6px 0;
	}

	.edit-events-container {
		display: flex;
		width: 100%;
		justify-content: space-between;
		align-items: center;

		.actions {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 10px;

			.counter {
				color: var(--txt-1);
			}
		}
	}
}

.webhook-container {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;

	.webhook-data-edit {
		display: flex;
		align-items: flex-end;
		margin-left: auto;
		flex-direction: column;
		gap: 8px;
		max-width: 50%;

		.webhook-data {
			display: flex;
			flex-direction: column;
			gap: 8px;
			padding: 10px;
			border-radius: 13px;
			background-color: var(--color-alpha-1);
			width: 100%;

			.webhook-data-headline {
				gap: 10px;

				.webhook-data-type {
					display: flex;
					flex-direction: column;
					justify-content: center;
					align-items: flex-start;
				}
			}


			.events-scroller {
				display: flex;
				flex-direction: row;
				flex-wrap: nowrap;
				align-items: center;
				padding: 10px;
				gap: 10px;
				background: var(--color-alpha-1);
				box-shadow: inset 0 1px 2px var(--color-alpha-4);
				border-radius: 13px;
				overflow-x: auto;

				.event {
					padding: 10px 15px;
					background: var(--color-alpha-1);
					border-radius: 13px;
					white-space: nowrap;
				}
			}

			@media (max-width: 960px) {
				flex-direction: column;
				align-items: flex-end;
			}

			&-type {
				display: flex;
				align-self: stretch;
				align-items: center;
				border-radius: 13px;
				padding: 0 10px 0 10px;
				background-color: var(--color-bg-primary-2);
				box-shadow: inset 0 2px 3px var(--color-alpha-4);

				@media (max-width: 960px) {
					padding: 15px 10px;
				}
			}

			&-url {
				color: var(--color-svg-grey);
				max-width: 250px;
				@include truncateString(1);

				@media (max-width: 720px) {
					max-width: 130px;
				}
			}

			&-auth {
				font-size: 12px;
				color: var(--color-svg-grey);
				@include textSelect();
			}

		}

		.bin-container {
			display: flex;
			align-items: center;
			justify-content: center;
			margin-left: 8px;
			cursor: pointer;
			transition: opacity 0.2s ease-in-out;

			&.loading {
				pointer-events: none;
				opacity: 0.5;
			}

			&:deep(svg) {
				width: 24px;
				height: 24px;
				padding: 4px;

				.fill {
					fill: var(--color-svg-grey);
				}

				.stroke {
					stroke: var(--color-svg-grey);
				}
			}
		}
	}

	@media (max-width: 550px) {
		flex-direction: column;
		align-items: flex-start;

		.webhook-data-edit {
			max-width: 100%;
		}
	}
}
@media (max-width: 360px) {
	.input-wrapper {
		width: 200px;
	}
}
</style>
